/**
 * MapboxLocationSelector - Geocoding autocomplete for structured location data
 * Uses Mapbox Geocoding API for city autocomplete
 * Extracts: city, city_id, state_name, state_code, country_name, country_code, lat, lng
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, MapPin, Loader2, X, Check } from 'lucide-react';
import { Input } from './ui/input';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
};

// Extract location data from Mapbox feature
const extractLocationData = (feature) => {
  const data = {
    city: feature.text || '',
    city_id: feature.id || '',
    state_name: '',
    state_code: '',
    country_name: '',
    country_code: '',
    lat: feature.center?.[1] || null,
    lng: feature.center?.[0] || null,
  };
  
  // Parse context for region and country
  if (feature.context) {
    for (const ctx of feature.context) {
      if (ctx.id.startsWith('region')) {
        data.state_name = ctx.text || '';
        data.state_code = ctx.short_code?.toUpperCase() || '';
      }
      if (ctx.id.startsWith('country')) {
        data.country_name = ctx.text || '';
        data.country_code = ctx.short_code?.toUpperCase() || '';
      }
    }
  }
  
  // Handle case where feature itself is a region or country
  if (feature.place_type?.includes('region') && !data.state_name) {
    data.state_name = feature.text;
    data.state_code = feature.properties?.short_code?.toUpperCase() || '';
  }
  
  if (feature.place_type?.includes('country') && !data.country_name) {
    data.country_name = feature.text;
    data.country_code = feature.properties?.short_code?.toUpperCase() || '';
  }
  
  return data;
};

const MapboxLocationSelector = ({ 
  value = null, 
  onChange, 
  placeholder = 'Cerca città...',
  label = 'Città',
  required = false,
  error = null,
  className = ''
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const debouncedQuery = useDebounce(query, 300);
  
  // Search Mapbox API
  const searchLocations = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    
    if (!MAPBOX_TOKEN) {
      console.error('MAPBOX_TOKEN not configured');
      setResults([]);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const encodedQuery = encodeURIComponent(searchQuery);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?access_token=${MAPBOX_TOKEN}&types=place&autocomplete=true&limit=8`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.features) {
        setResults(data.features);
        setIsOpen(true);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Mapbox search error:', err);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && !value) {
      searchLocations(debouncedQuery);
    }
  }, [debouncedQuery, value, searchLocations]);
  
  // Handle selection
  const handleSelect = (feature) => {
    const locationData = extractLocationData(feature);
    onChange(locationData);
    setQuery(feature.place_name || feature.text);
    setResults([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  
  // Handle clear
  const handleClear = () => {
    setQuery('');
    onChange(null);
    setResults([]);
    inputRef.current?.focus();
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    // Clear selection if user types
    if (value) {
      onChange(null);
    }
    
    if (!newQuery) {
      setResults([]);
      setIsOpen(false);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Format display value
  const displayValue = value 
    ? `${value.city}${value.state_name ? `, ${value.state_name}` : ''}${value.country_name ? `, ${value.country_name}` : ''}`
    : query;
  
  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
          {label} {required && '*'}
        </label>
      )}
      
      {/* Input Container */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </div>
        
        <Input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true);
            if (results.length > 0) setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`pl-10 pr-10 bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-sr-gold ${
            error ? 'border-red-500' : ''
          } ${value ? 'border-emerald-500/50' : ''}`}
          autoComplete="off"
          data-testid="mapbox-location-input"
        />
        
        {/* Clear/Selected indicator */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {value ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-emerald-400 hover:text-white transition-colors"
            >
              <Check className="w-4 h-4" />
            </button>
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-white/40 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
      
      {/* Selected location info */}
      {value && (
        <div className="mt-2 flex items-center gap-2 text-xs text-emerald-400">
          <MapPin className="w-3 h-3" />
          <span>
            {value.city}
            {value.state_code && ` (${value.state_code})`}
            {value.country_code && ` - ${value.country_code}`}
          </span>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      )}
      
      {/* Dropdown Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a2e] border border-white/20 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
          {results.map((feature, idx) => {
            const locationData = extractLocationData(feature);
            return (
              <button
                key={feature.id || idx}
                type="button"
                onClick={() => handleSelect(feature)}
                className="w-full px-4 py-3 text-left hover:bg-sr-gold/20 transition-colors border-b border-white/5 last:border-0"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-sr-gold-light mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">
                      {feature.text}
                    </p>
                    <p className="text-xs text-white/50 truncate">
                      {locationData.state_name && `${locationData.state_name}, `}
                      {locationData.country_name}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      
      {/* No results */}
      {isOpen && query.length >= 2 && results.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a2e] border border-white/20 rounded-lg shadow-xl p-4 text-center">
          <p className="text-sm text-white/50">Nessun risultato trovato</p>
        </div>
      )}
      
      {/* No Mapbox Token Warning */}
      {!MAPBOX_TOKEN && isFocused && (
        <div className="absolute z-50 w-full mt-1 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-xs text-red-400">
            Mapbox API non configurata. Aggiungi REACT_APP_MAPBOX_TOKEN in .env
          </p>
        </div>
      )}
    </div>
  );
};

export default MapboxLocationSelector;
